import { Poll, VotePollRequest } from '../../Types/poll';
import { useVotePoll, useTogglePollActive } from '../../Hooks/usePollHooks';
//import { formatDate, formatToSimpleTimeString } from '../../Utils/dateUtils';
import { BizButton } from '../Mini/BizButton';
import BizIconButton from '../Mini/BizIconButton';
import { useGlobalState } from '../../Contexts/globalStateContext';
import { hasModuleAccess } from '../../Services/access';
import { AccessTypeRight } from '../../Types/access';
import './PollDisplay.scss';
import PollEdit from '../../../Admin/Polls/PollEdit';
import { useTranslation } from 'react-i18next';

interface PollDisplayProps {
	poll: Poll;
	readonly?: boolean;
}

const PollDisplay = ({ poll, readonly = false }: PollDisplayProps) => {
	const { t } = useTranslation();
	const {
		state: { user },
		showModal,
		hideModal,
	} = useGlobalState();
	const { votePoll, statusState: pollVoteStatusState } = useVotePoll(); //, statusState: voteStatusState
	const { togglePollActive, statusState: toggleStatusState } = useTogglePollActive();

	const isAdmin = hasModuleAccess(user, 'POLL', AccessTypeRight.Write);
	const canVote = !readonly && !poll.UserHasVoted && poll.IsActive;
	const showResults = poll.UserHasVoted || readonly || !poll.IsActive;

	if (!poll.IsActive && !isAdmin) {
		return null; // Don't show inactive polls to non-admins
	}

	const handleVote = (alternativeId: number) => {
		if (!canVote) return;
		if (pollVoteStatusState === 'LOADING') return; // Prevent multiple votes while loading

		const voteRequest: VotePollRequest = {
			PollId: poll.Id,
			AlternativeId: alternativeId,
		};
		votePoll(voteRequest);
	};

	const handleToggleActive = () => {
		if (!isAdmin) return;
		togglePollActive(poll.Id);
	};

	const handleEdit = () => {
		// TODO: Implement edit modal
		console.log('Edit poll:', poll.Id);
		showModal(
			<PollEdit
				pollId={poll.Id}
				//onSave={handleCreatePoll}
				onClose={hideModal}
				//statusState={createStatusState}
			/>,
			t('POLL_DISPLAY.EDIT_POLL', { defaultValue: 'Edit poll' })
		);
	};

	const formatCreationInfo = () => {
		// const dateStr = formatDate(poll.CreationDate, 'dd MMMM');
		// const timeStr = formatToSimpleTimeString(poll.CreationDate);
		const voteText =
			poll.TotalVotes === 1
				? t('POLL_DISPLAY.VOTE_SINGLE', { defaultValue: 'vote' })
				: t('POLL_DISPLAY.VOTE_PLURAL', { defaultValue: 'votes' });
		//return `Created ${dateStr} ${timeStr}, ${poll.TotalVotes} ${voteText}`;
		return `${poll.TotalVotes} ${voteText}`;
	};

	return (
		<div className={`biz-poll-display ${!poll.IsActive ? 'inactive' : ''}`}>
			<div className='poll-header'>
				<h4 className='poll-question'>{poll.Question}</h4>
				{isAdmin && (
					<div className='poll-admin-actions'>
						{!poll.IsActive && (
							<span className='poll-inactive-notice'>
								{t('POLL_DISPLAY.INACTIVE', { defaultValue: 'Inactive' })}
							</span>
						)}
						<BizIconButton
							icon='EDIT'
							onClick={handleEdit}
							legacy
							title={t('POLL_DISPLAY.EDIT_POLL', { defaultValue: 'Edit poll' })}
						/>
						<BizButton
							text={
								poll.IsActive
									? t('POLL_DISPLAY.DEACTIVATE', { defaultValue: 'Deactivate' })
									: t('POLL_DISPLAY.ACTIVATE', { defaultValue: 'Activate' })
							}
							onClick={handleToggleActive}
							state={toggleStatusState}
							legacy
							className='biz-button'
						/>
					</div>
				)}
			</div>

			<div className='poll-info'>{formatCreationInfo()}</div>

			<div className='poll-alternatives'>
				{poll.Alternatives.map((alternative) => (
					<div
						key={alternative.Id}
						className={`poll-alternative ${canVote ? 'can-vote' : ''} ${!isAdmin ? 'user' : 'admin'}`}
						onClick={canVote ? () => handleVote(alternative.Id) : undefined}
					>
						<div className='vote-stats'>
							<span className='vote-count'>{alternative.Alternative}</span>
							<span className='vote-percentage'>
								{(showResults || isAdmin) && (
									<>
										{alternative.VoteCount} ({alternative.VotePercentage.toFixed(0)}%)
									</>
								)}
							</span>
						</div>
						{(showResults || isAdmin) && (
							<div className='alternative-results'>
								<div className='percentage-bar' style={{ width: `${alternative.VotePercentage}%` }}></div>
							</div>
						)}
					</div>
				))}
			</div>
		</div>
	);
};

export default PollDisplay;
